<template>
	<main class="m-aboutus" :lang="pageLang">
		<HeroBanner ref="heroBannerRef" />

		<article class="m-aboutus__content">
			<BrandPhilosophySection />

			<section ref="relatedNews" class="m-aboutus__section">
				<NewsReleaseSection :list="pressReleaseList" :loading="pressReleasePending" />
			</section>

			<section ref="cooperateRef" class="m-aboutus__section">
				<BusinessSection :list="businessOfficesList" />
			</section>

			<section class="m-aboutus__section">
				<CooperativeMediaSection />
			</section>

			<DownloadSection
				:apk-link="apkLink"
				@android-click="toAppStore('android')"
				@ios-click="toAppStore('ios')"
				@direct-download="handleDirectDownload"
			/>
		</article>
	</main>
</template>

<script setup>
import { getAboutUsNewsList, getDownloadApk } from "~/api/api";
import { computed, onMounted, ref, watch } from "vue";
import { downloadApp } from "~/utils/common";
import { useI18n } from "~/composables/useI18n";
import { ABOUT_SECTION_ID, useAboutUsTracking } from "../composables/useAboutUsTracking";
import { useAboutUsAnchorScroll } from "../composables/useAboutUsAnchorScroll";
import businessPicHk from "../images/shangwu1.png";
import businessPicTw from "../images/shangwu2.png";
import businessPicMy from "../images/shangwu3.png";
import HeroBanner from "./components/hero-banner/index.vue";
import BrandPhilosophySection from "./components/brand-philosophy-section/index.vue";
import NewsReleaseSection from "./components/news-release/index.vue";
import BusinessSection from "./components/business-section/index.vue";
import CooperativeMediaSection from "./components/cooperative-media-section/index.vue";
import DownloadSection from "./components/download-section/index.vue";

useMentorJoinFont();

const I18N_NS = "aboutUs";
const { t, locale, trackTranslationDeps } = useI18n();
const { track, observeImpression } = useAboutUsTracking("mobile");
const pageLang = computed(() => (locale?.value === "en" ? "en" : "zh-TW"));
const heroBannerRef = ref(null);

const BUSINESS_OFFICE_IMAGES = [businessPicHk, businessPicTw, businessPicMy];
const OFFICE_CONTACT_COUNTS = [5, 4, 3];

const businessOfficesList = computed(() => {
	trackTranslationDeps();
	return BUSINESS_OFFICE_IMAGES.map((image, officeIndex) => ({
		title: t(`${I18N_NS}.business.offices.${officeIndex}.title`),
		image,
		contacts: Array.from({ length: OFFICE_CONTACT_COUNTS[officeIndex] }, (_, contactIndex) => ({
			icon: t(`${I18N_NS}.business.offices.${officeIndex}.contacts.${contactIndex}.icon`),
			text: t(`${I18N_NS}.business.offices.${officeIndex}.contacts.${contactIndex}.text`),
		})),
	}));
});

useHead(() => ({
	title: t(`${I18N_NS}.meta.title`),
	meta: [
		{ name: "keywords", content: t(`${I18N_NS}.meta.keywords`) },
		{ name: "description", content: t(`${I18N_NS}.meta.description`) },
		{ property: "og:title", content: t(`${I18N_NS}.meta.ogTitle`) },
		{ property: "og:description", content: t(`${I18N_NS}.meta.ogDescription`) },
		{ property: "og:type", content: "website" },
	],
}));

const relatedNews = ref(null);
const cooperateRef = ref(null);
const elMap = ref({
	newsroom: relatedNews,
	cooperate: cooperateRef,
});
const { scrollToTarget } = useAboutUsAnchorScroll(elMap, {
	offset: 56,
});

const normalizeNewsTimestamp = value => {
	if (!value) {
		return 0;
	}

	const numericValue = Number(value);
	if (!Number.isNaN(numericValue) && numericValue > 0) {
		return numericValue < 1000000000000 ? numericValue * 1000 : numericValue;
	}

	const parsedTime = new Date(value).getTime();
	return Number.isNaN(parsedTime) ? 0 : parsedTime;
};

const formatNewsDate = value => {
	const timestamp = normalizeNewsTimestamp(value);
	if (!timestamp) {
		return "";
	}

	const date = new Date(timestamp);
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const mapNewsItem = item => {
	const publishTime = item?.publish_time || item?.created_time || 0;

	return {
		...item,
		id: item?.id || item?.resource_id || item?.adv_id || "",
		title: item?.name || item?.title || item?.description || "",
		date: formatNewsDate(publishTime),
		banner:
			item?.m_cover ||
			item?.pc_cover ||
			item?.cover ||
			item?.cover_en ||
			item?.cover_cc ||
			item?.banner ||
			"",
		link: item?.actioncontent?.global_type || item?.link || "",
		publish_time: normalizeNewsTimestamp(publishTime),
	};
};

const { data: pressReleaseList, pending: pressReleasePending } = useLazyAsyncData(
	"aboutus-mobile-news-release",
	async () => {
		try {
			const response = await getAboutUsNewsList();
			const rawList = Array.isArray(response)
				? response
				: Array.isArray(response?.data)
					? response.data
					: [];

			return rawList
				.map(mapNewsItem)
				.filter(item => item.title && item.link)
				.sort((a, b) => (b.publish_time ?? 0) - (a.publish_time ?? 0));
		} catch (error) {
			console.error("获取新闻发布列表失败:", error);
			return [];
		}
	},
	{
		default: () => [],
	}
);

watch(
	pressReleaseList,
	(list, prev) => {
		if ((prev?.length ?? 0) === 0 && (list?.length ?? 0) > 0) {
			scrollToTarget();
		}
	},
	{ flush: "post" }
);

const { data: apkLink } = useLazyAsyncData("aboutus-mobile-downloadApk", async () => {
	const res = await getDownloadApk();
	return res.data.link;
});

const handleDirectDownload = () => {
	if (!import.meta.client) return;
	track("app_download_click", {
		store_type: "apk",
		section_id: ABOUT_SECTION_ID.APP_DOWNLOAD,
	});
};

onMounted(() => {
	track("about_page_view");
	observeImpression("about_hero_view", heroBannerRef, () => {
		track("about_hero_view", {
			section_id: ABOUT_SECTION_ID.HERO,
			banner_id: "about_hero_banner",
		});
	});
	scrollToTarget();
});

const toAppStore = type => {
	if (!import.meta.client) return;
	track("app_download_click", {
		store_type: type === "android" ? "google_play" : "app_store",
		section_id: ABOUT_SECTION_ID.APP_DOWNLOAD,
	});
	downloadApp(type);
};
</script>

<style lang="scss" scoped>
.m-aboutus {
	background-color: #ffffff;
	font-family: "Source Han Serif CN", serif;
	overflow: hidden;

	&__content {
		background: #ffffff;
	}

	&__section {
		background: #ffffff;
	}
}
</style>
